// CTSK-500 — client side of the upload batch id.
//
// ONE id per drop / picker ACTION (not per file): every file of the action
// carries it as the `ingestBatchId` multipart field so the API can stamp
// documents.ingest_batch_id. Rules:
//   • single-file action → no batch id (CTSK-501: except a DIRECTORY drop,
//     which always gets one — see batchIdForDirectoryDrop);
//   • grouping off (env flag or org setting) → no batch id (the API re-checks
//     and would ignore it anyway, but the client does not send what it knows
//     will be dropped);
//   • the id is decided ONCE when the action starts and stored with each file,
//     so concurrency (3 at a time) and upload order cannot change it.
//
// Format: ULID — 10 chars of 48-bit millisecond time + 16 chars of 80-bit
// randomness, Crockford base32 (no I L O U), 26 chars, upper case. Matches
// INGEST_BATCH_ID_REGEX in @captain-tasko/shared. Uniqueness within an org is
// all that matters; it is a label, never an authorization input.

import {
  INGEST_BATCH_ID_FIELD,
  INGEST_BATCH_ID_REGEX,
  SOURCE_FOLDER_FIELD,
  normalizeSourceFolder,
} from '@captain-tasko/shared';

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

function randomBytes(n: number): Uint8Array {
  const out = new Uint8Array(n);
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c?.getRandomValues) c.getRandomValues(out);
  else for (let i = 0; i < n; i++) out[i] = Math.floor(Math.random() * 256);
  return out;
}

/** A fresh ULID. `now` and `random` are injectable for tests. */
export function newIngestBatchId(now: number = Date.now(), random: Uint8Array = randomBytes(16)): string {
  let t = Math.max(0, Math.floor(now));
  let time = '';
  for (let i = 0; i < 10; i++) {
    time = CROCKFORD[t % 32]! + time;
    t = Math.floor(t / 32);
  }
  let rand = '';
  for (let i = 0; i < 16; i++) rand += CROCKFORD[(random[i] ?? 0) & 31];
  return time + rand;
}

/**
 * Shared minting rule: nothing when grouping is off or the action has fewer
 * than `minFiles` files, otherwise a fresh id.
 */
function mintBatchId(
  fileCount: number,
  groupingEnabled: boolean,
  minFiles: number,
  generate: () => string,
): string | null {
  if (!groupingEnabled || fileCount < minFiles) return null;
  return generate();
}

/**
 * The batch id for one upload action, or null. Call ONCE per action and keep
 * the result with every file of that action. Plain file drops and picks need
 * at least 2 files (Stage 2a's floor).
 */
export function batchIdForAction(
  fileCount: number,
  groupingEnabled: boolean,
  generate: () => string = newIngestBatchId,
): string | null {
  return mintBatchId(fileCount, groupingEnabled, 2, generate);
}

/**
 * CTSK-501 — the batch id for one DIRECTORY drop / folder pick. A folder's
 * presence is itself the grouping signal, so this deliberately bypasses the
 * 2-file floor: a folder holding a single file still gets a batch id. Plain
 * file drops keep using batchIdForAction (floor unchanged).
 */
export function batchIdForDirectoryDrop(
  fileCount: number,
  groupingEnabled: boolean,
  generate: () => string = newIngestBatchId,
): string | null {
  return mintBatchId(fileCount, groupingEnabled, 1, generate);
}

/** Multipart fields for apiUpload (appended BEFORE the file — see apiUpload). */
export function batchFields(batchId: string | null | undefined): Record<string, string> | undefined {
  return batchId && INGEST_BATCH_ID_REGEX.test(batchId) ? { [INGEST_BATCH_ID_FIELD]: batchId } : undefined;
}

/**
 * CTSK-501 — multipart fields for one file of a directory drop: the batch id
 * (when there is one) and the relative source folder (when it survives the
 * shared sanitizer — the server re-checks, this is a courtesy). Key order is
 * ingestBatchId then sourceFolder; apiUpload appends them before the file.
 * Returns undefined when there is nothing to send, so plain uploads are
 * byte-for-byte what they were.
 */
export function uploadFields(
  batchId: string | null | undefined,
  sourceFolder: string | null | undefined,
): Record<string, string> | undefined {
  const fields: Record<string, string> = { ...(batchFields(batchId) ?? {}) };
  const folder = normalizeSourceFolder(sourceFolder);
  if (folder) fields[SOURCE_FOLDER_FIELD] = folder;
  return Object.keys(fields).length > 0 ? fields : undefined;
}

/** Library URL filtered to one batch (used by "View batch in library"). */
export function batchLibraryHref(batchId: string): string {
  return `/documents?ingestBatch=${encodeURIComponent(batchId)}`;
}

/** CTSK-501 — Library URL filtered to one source folder, exact match (used by "View folder in library"). */
export function folderLibraryHref(folder: string): string {
  return `/documents?folder=${encodeURIComponent(folder)}`;
}
